import { z } from "zod";

// Closed-set Databricks values used by the Run History filters. Lives in
// a sibling file (mirrors `./chartTypes`, `./sparkTypes`) to keep
// `./types.ts` under the per-file line cap.

export const DATABRICKS_RESULT_STATE = {
  SUCCEEDED: "SUCCEEDED",
  FAILED: "FAILED",
  TIMED_OUT: "TIMED_OUT",
  CANCELLED: "CANCELLED",
  ERROR: "ERROR",
  SKIPPED: "SKIPPED",
  BLOCKED: "BLOCKED",
} as const;

export type DatabricksResultState =
  (typeof DATABRICKS_RESULT_STATE)[keyof typeof DATABRICKS_RESULT_STATE];

export const DATABRICKS_TRIGGER_TYPE = {
  CRON: "CRON",
  ONETIME: "ONETIME",
  ONETIME_RETRY: "ONETIME_RETRY",
  CONTINUOUS: "CONTINUOUS",
  FILE_ARRIVAL: "FILE_ARRIVAL",
} as const;

export type DatabricksTriggerType =
  (typeof DATABRICKS_TRIGGER_TYPE)[keyof typeof DATABRICKS_TRIGGER_TYPE];

export const DATABRICKS_TRIGGER_LABEL: Record<DatabricksTriggerType, string> = {
  CRON: "Scheduled",
  ONETIME: "Manual",
  ONETIME_RETRY: "Manual Retry",
  CONTINUOUS: "Continuous",
  FILE_ARRIVAL: "File Arrival",
};

export const DATABRICKS_RESULT_STATE_OPTIONS: Array<{
  label: string;
  value: DatabricksResultState;
}> = [
  { label: "Succeeded", value: DATABRICKS_RESULT_STATE.SUCCEEDED },
  { label: "Failed", value: DATABRICKS_RESULT_STATE.FAILED },
  { label: "Error", value: DATABRICKS_RESULT_STATE.ERROR },
  { label: "Timed Out", value: DATABRICKS_RESULT_STATE.TIMED_OUT },
  { label: "Cancelled", value: DATABRICKS_RESULT_STATE.CANCELLED },
  { label: "Skipped", value: DATABRICKS_RESULT_STATE.SKIPPED },
  { label: "Blocked", value: DATABRICKS_RESULT_STATE.BLOCKED },
];

export const DATABRICKS_TRIGGER_OPTIONS: Array<{
  label: string;
  value: DatabricksTriggerType;
}> = [
  { label: DATABRICKS_TRIGGER_LABEL.CRON, value: DATABRICKS_TRIGGER_TYPE.CRON },
  { label: DATABRICKS_TRIGGER_LABEL.ONETIME, value: DATABRICKS_TRIGGER_TYPE.ONETIME },
  { label: DATABRICKS_TRIGGER_LABEL.ONETIME_RETRY, value: DATABRICKS_TRIGGER_TYPE.ONETIME_RETRY },
  { label: DATABRICKS_TRIGGER_LABEL.CONTINUOUS, value: DATABRICKS_TRIGGER_TYPE.CONTINUOUS },
  { label: DATABRICKS_TRIGGER_LABEL.FILE_ARRIVAL, value: DATABRICKS_TRIGGER_TYPE.FILE_ARRIVAL },
];

// Cluster filter dropdown — sourced from `GET /jobs/{job_id}/runs/clusters`,
// not derived from the runs page.
export const JobRunClusterSchema = z.object({
  cluster_id: z.string(),
  // null when the cluster has been deleted from `dbx_compute_clusters`; the
  // dropdown falls back to the id in that case.
  cluster_name: z.string().nullish(),
});

export const JobRunClustersResponseSchema = z.object({
  clusters: z.array(JobRunClusterSchema),
});

export type JobRunCluster = z.infer<typeof JobRunClusterSchema>;
export type JobRunClustersResponse = z.infer<typeof JobRunClustersResponseSchema>;
